import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ViewChild,
    Input,
    HostBinding,
    HostListener,
    forwardRef,
    EventEmitter,
    Output,
    ElementRef,
    inject,
    DestroyRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MultiSelectComponent, DropDownSize } from "@progress/kendo-angular-dropdowns";
import { TooltipDirective } from "@progress/kendo-angular-tooltip";
import { WkTranslatePipe, NestedValuePipe } from "@shared/utils";
import { ITranslateObject } from "../../../views-interfaces";

declare module "@progress/kendo-angular-dropdowns" {
    interface MultiSelectComponent {
        name: string;
        focused?: boolean;
        blured?: boolean;
        clicked?: boolean;
        scrolled?: boolean;
        arrowDownKeyPress: boolean;
    }
}

@Component({
    selector: "kendo-multiselect-wrapper",
    templateUrl: "./kendo-multiselect-wrapper.component.html",
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "wk-msw cg3-host-multiselect",
        "cg3-theme": "PDS-v3",
    },
    providers: [
        WkTranslatePipe,
        NestedValuePipe,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => KendoMultiselectWrapper),
            multi: true,
        },
    ],
})
class KendoMultiselectWrapper implements ControlValueAccessor {
    private wkTranslate = inject(WkTranslatePipe);
    private nestedValue = inject(NestedValuePipe);
    private hostElement = inject(ElementRef);
    private cdr = inject(ChangeDetectorRef);
    private searchbarKeydownListener: ((event: KeyboardEvent) => void) | null = null;
    private hostKeydownListener: ((event: KeyboardEvent) => void) | null = null;

    constructor() {
        const destroyRef = inject(DestroyRef);
        destroyRef.onDestroy(() => {
            if (this.searchbarKeydownListener && this._multiselect?.searchbar?.input?.nativeElement) {
                this._multiselect.searchbar.input.nativeElement.removeEventListener("keydown", this.searchbarKeydownListener, true);
            }
            if (this.hostKeydownListener && this._multiselect?.hostElement?.nativeElement) {
                this._multiselect.hostElement.nativeElement.removeEventListener("keydown", this.hostKeydownListener, true);
            }
        });
    }

    @Input() name: string;
    @Input() textField = "text";
    @Input() valueField = "value";
    @Input() activeField = "active";
    @Input() size: DropDownSize = "medium";
    @Input() disabled = false;
    @Input() placeholder = "";
    @Input() noDataText = "";
    @Input() virtual = false;
    @Input() valuePrimitive = true;
    @Output() filterChange = new EventEmitter<string>();
    @Input() clearTitleText = "";
    @Input() label?: string | ITranslateObject = "";

    filteredData: any[] = [];
    private _data: any = null;
    @Input()
    set data(val: any) {
        this._data = val;
        this.filteredData = val;
    }
    get data() {
        return this._data;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.cdr.markForCheck();
    }

    handleValueChange(value: any): void {
        this.value = value;
        if (this._multiselect) {
            this._multiselect.value = value === "" ? null : value;
        }
        this.onChange(value);
        this.onTouched();
    }

    private onChange: (value: any) => void = () => {};
    private onTouched: () => void = () => {};

    private _multiselect!: MultiSelectComponent;
    @ViewChild(MultiSelectComponent, { static: false })
    set multiselect(component: MultiSelectComponent) {
        if (component && this._multiselect == null) {
            this._multiselect = component;
            this.initializeMultiSelectComponent(this._multiselect);
        }
    }
    get multiselect(): MultiSelectComponent {
        return this._multiselect;
    }

    private _value: any = null;
    @Input()
    set value(val: any) {
        this.writeValue(val);
    }
    get value() {
        return this._value;
    }

    writeValue(value: any): void {
        this._value = value || [];
        if (this._multiselect) {
            this._multiselect.writeValue(value);
        }
        this.cdr.markForCheck();
    }

    initializeMultiSelectComponent(component: MultiSelectComponent): void {
        component.name = this.name;
        component.focused = false;
        component.blured = false;
        component.clicked = false;
        component.scrolled = false;
        component.arrowDownKeyPress = false;

        if (component.valueChange) {
            component.valueChange.subscribe((value: any) => {
                this.handleValueChange(value);
            });
        }

        this.searchbarKeydownListener = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                if (this._multiselect.isOpen) this._multiselect.toggle(false);
                else this.hostElement.nativeElement.dispatchEvent(new KeyboardEvent("keydown", event));

                event.preventDefault();
                event.stopImmediatePropagation();
                event.stopPropagation();
            }
        };
        this._multiselect.searchbar.input.nativeElement.addEventListener("keydown", this.searchbarKeydownListener, true);

        this.hostKeydownListener = (event: KeyboardEvent) => {
            if (event.key === "Backspace") {
                const target = event.target as HTMLElement;
                const input = target as HTMLInputElement;
                const tagListElement = Array.from(target.parentElement?.children || []).find(
                    (child) => child.tagName === "KENDO-TAGLIST",
                ) as HTMLElement;
                if (
                    target.classList.contains("cg3-multiselect-body") &&
                    target.tagName === "INPUT" &&
                    input.value === "" &&
                    tagListElement &&
                    tagListElement?.children.length > 0
                ) {
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                }
            }
        };
        this._multiselect.hostElement.nativeElement.addEventListener("keydown", this.hostKeydownListener, true);

        if (component.onFocus && typeof component.onFocus.subscribe === "function") {
            component.onFocus.subscribe(() => {
                this.onMultiSelectFocus(component);
            });
        }

        if (component.onBlur && typeof component.onBlur.subscribe === "function") {
            component.onBlur.subscribe(() => {
                this.onMultiSelectBlur(component);
            });
        }

        if (component.opened && typeof component.opened.subscribe === "function") {
            component.opened.subscribe((event: any) => {
                this.onMultiSelectOpen(component);
            });
        }
        if (component.close && typeof component.close.subscribe === "function") {
            component.close.subscribe((event: any) => {
                this.onMultiSelectClose(component, event);
            });
        }
    }

    public onItemDisabled = (args: { dataItem: any; index: number }): boolean => {
        const item = args.dataItem;
        const keys = Object.keys(item);
        if (!this.activeField || !keys.includes(this.activeField)) return false;

        return item[this.valueField] !== this.value && !item[this.activeField];
    };

    toggleDropdown(): void {
        if (!this._multiselect) return;

        this._multiselect.clicked = true;
        this._multiselect.toggle(!this._multiselect.isOpen);
        if (!this._multiselect.isOpen) this._multiselect.searchbar.focus();
        this.cdr.markForCheck();
    }

    onMultiSelectBlur(select: MultiSelectComponent): void {
        select.blured = true;
        select.toggle(false);
        this.cdr.markForCheck();
    }

    onMultiSelectFocus(select: MultiSelectComponent): void {
        select.focused = true;
        select.blured = false;
        this.cdr.markForCheck();
    }

    onMultiSelectOpen(select: MultiSelectComponent): void {
        select.clicked = false;
        select.focusItemAt(-1);
        select.arrowDownKeyPress = false;
        this.cdr.markForCheck();
        setTimeout(() => {
            select?.hostElement?.nativeElement?.querySelector(".k-input")?.focus();
        }, 0);
    }

    onMultiSelectClose(select: MultiSelectComponent, event: any): void {
        if (!select.scrolled && (!select.clicked || (select.focused && !select.blured))) event.preventDefault();
        select.clicked = false;
        select.scrolled = false;
        this.cdr.markForCheck();
    }

    @HostBinding("class.wk-msw--open")
    get isDropdownOpen() {
        return this._multiselect ? this._multiselect.isOpen : false;
    }

    onFilterChange(filter: string): void {
        if (filter === undefined || filter === null || filter === "") {
            this.filteredData = this.data;
            if (this.filterChange.observed) this.filterChange.emit(filter);
            return;
        }

        filter = this.normalizeText(filter);

        this.filteredData = this.data.filter((item) => {
            const text = this.normalizeText(this.wkTranslate.transform(this.nestedValue.transform(item, this.textField)));
            const includes = text.includes(filter);
            return includes;
        });

        if (this.filterChange.observed) this.filterChange.emit(filter);
    }

    private normalizeText(text: string): string {
        if (text == null) return "";
        return text
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .trim();
    }

    @HostBinding("attr.size")
    get hostSize() {
        return this.size;
    }

    @HostListener("keydown.escape", ["$event"])
    onKeydownUp(event: Event) {
        if (this._multiselect && this._multiselect.isOpen) {
            this._multiselect.toggle(false);
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
        }
    }

    @HostListener("document:keydown.escape", ["$event"])
    onKeydownHandler(event: Event) {
        if (this._multiselect && this._multiselect.isOpen) {
            this._multiselect.toggle(false);
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
        }
    }

    @HostListener("document:keydown.tab", ["$event"])
    onKeydownTabHandler(event: Event) {
        if (this._multiselect && this._multiselect.isOpen) {
            this._multiselect.toggle(false);
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
        }
    }

    @ViewChild(TooltipDirective) tooltipDir!: TooltipDirective;
    private showTimeout: any;
    private hideTimeout: any;

    onMouseOver(e: MouseEvent): void {
        const target = e.target as HTMLElement | null;
        const el = target?.closest("li.k-list-item") as HTMLElement | null;

        if (!el) {
            this.tooltipDir?.hide();
            return;
        }

        const textEl = el.querySelector<HTMLElement>(".multiselect-wrapper-item") ?? el;
        const isOverflowing = textEl.scrollWidth > textEl.clientWidth || textEl.scrollHeight > textEl.clientHeight;

        if (isOverflowing) {
            clearTimeout(this.hideTimeout);
            clearTimeout(this.showTimeout);
            this.showTimeout = setTimeout(() => {
                this.tooltipDir.toggle(el, true);
            }, 600);
        } else {
            this.tooltipDir.hide();
        }
    }

    onMouseLeave(_: MouseEvent) {
        clearTimeout(this.showTimeout);

        const tooltipEl = document.querySelector(".kendo-multiselect-wrapper-tooltip.k-animation-container") as HTMLElement;
        if (tooltipEl) {
            tooltipEl.classList.add("cg3-tooltip-closing");
        }

        this.hideTimeout = setTimeout(() => {
            this.tooltipDir.hide();
        }, 300);
    }

    @HostListener("keydown.arrowdown", ["$event"])
    onArrowDown(_: Event): void {
        if (!this.multiselect.open) {
            this.multiselect.toggle(true);
            return;
        }

        if (this.multiselect?.arrowDownKeyPress) return;

        this.multiselect.focusItemAt(0);
        this.multiselect.arrowDownKeyPress = true;
    }
}

export { KendoMultiselectWrapper };
